/**
 * Custom hook for patient form logic
 * Handles NPI lookup, ICD-10 search, and eligibility checking
 */
import { useState } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { emptyPatient } from '../constants/patientConstants';

export const usePatientForm = () => {
  const [formData, setFormData] = useState(emptyPatient);
  const [saving, setSaving] = useState(false);
  
  // NPI Lookup state
  const [npiSearchResults, setNpiSearchResults] = useState([]);
  const [npiSearching, setNpiSearching] = useState(false);
  const [showNpiResults, setShowNpiResults] = useState(false);
  
  // ICD-10 Lookup state
  const [icd10SearchResults, setIcd10SearchResults] = useState([]);
  const [icd10Searching, setIcd10Searching] = useState(false);
  const [showIcd10Results, setShowIcd10Results] = useState(false);
  const [activeIcd10Field, setActiveIcd10Field] = useState('primary');
  
  // Eligibility check state
  const [checkingEligibility, setCheckingEligibility] = useState(false);

  const resetForm = () => {
    setFormData(emptyPatient);
    setNpiSearchResults([]);
    setShowNpiResults(false);
    setIcd10SearchResults([]);
    setShowIcd10Results(false);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // NPI Lookup function
  const handleNpiSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setNpiSearchResults([]);
      setShowNpiResults(false);
      return;
    }
    
    setNpiSearching(true);
    try {
      const isNpiNumber = /^\d{10}$/.test(searchTerm);
      const params = isNpiNumber 
        ? { npi: searchTerm }
        : { last_name: searchTerm, state: 'OH' };
      
      const response = await api.get('/lookup/npi', { params });
      setNpiSearchResults(response.data.results || []);
      setShowNpiResults(true);
    } catch (error) {
      toast.error('NPI lookup failed');
      setNpiSearchResults([]);
    } finally {
      setNpiSearching(false);
    }
  };

  const handleSelectNpi = (provider) => {
    setFormData(prev => ({
      ...prev,
      physician_npi: provider.npi,
      physician_name: provider.name,
      physician_specialty: provider.specialty || '',
    }));
    setShowNpiResults(false);
    setNpiSearchResults([]);
    toast.success(`Selected: ${provider.name}`);
  };

  // ICD-10 Lookup function
  const handleIcd10Search = async (searchTerm, field = 'primary') => {
    setActiveIcd10Field(field);
    
    if (!searchTerm || searchTerm.length < 2) {
      setIcd10SearchResults([]);
      setShowIcd10Results(false);
      return;
    }
    
    setIcd10Searching(true);
    try {
      const response = await api.get('/lookup/icd10', { params: { query: searchTerm } });
      setIcd10SearchResults(response.data.results || []);
      setShowIcd10Results(true);
    } catch (error) {
      toast.error('ICD-10 lookup failed');
      setIcd10SearchResults([]);
    } finally {
      setIcd10Searching(false);
    }
  };

  const handleSelectIcd10 = (code) => {
    if (activeIcd10Field === 'primary') {
      setFormData(prev => ({
        ...prev,
        primary_icd10_code: code.code,
        primary_icd10_description: code.description,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        secondary_icd10_code: code.code,
        secondary_icd10_description: code.description,
      }));
    }
    setShowIcd10Results(false);
    setIcd10SearchResults([]);
    toast.success(`Selected: ${code.code}`);
  };

  // Eligibility check function — surfaces the rich result to the caller
  // (which opens `EligibilityResultDialog`). We deliberately do NOT toast
  // the status here; a one-line toast loses 90% of the response (plan,
  // copays, benefits, registry cross-check). On hard failures only the
  // error toast still fires.
  const handleCheckEligibility = async (patientId) => {
    if (!patientId) {
      toast.error('Save patient first to check eligibility');
      return null;
    }

    setCheckingEligibility(true);
    try {
      const response = await api.post(`/patients/${patientId}/check-eligibility`);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eligibility check failed');
      return null;
    } finally {
      setCheckingEligibility(false);
    }
  };

  return {
    formData,
    setFormData,
    updateField,
    resetForm,
    saving,
    setSaving,
    // NPI
    npiSearchResults,
    npiSearching,
    showNpiResults,
    setShowNpiResults,
    handleNpiSearch,
    handleSelectNpi,
    // ICD-10
    icd10SearchResults,
    icd10Searching,
    showIcd10Results,
    setShowIcd10Results,
    activeIcd10Field,
    handleIcd10Search,
    handleSelectIcd10,
    // Eligibility
    checkingEligibility,
    handleCheckEligibility,
  };
};
